import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { api } from '../../api';
import type { StaffPendingApproval, StaffRoleGrant, StaffSessionSummary } from '../../api/areas/admin';
import type { AuditLogEntry } from '../../api/types';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { formatDateTime, formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { AccessChangesPanel, StaffAccessActions } from '../operator/AccessChanges';
import { ActionLabel, AuditReason, EntryDrawer, OUTCOME_TONE } from './AuditPage';
import { SignInBadges, STATUS_TONE } from './StaffPage';
import { RecordRef, useAdminCrumbs, useCatalogue } from './ui';

/** `/admin/staff/:id`: one staff account — what it may do, where it is signed in, what it did. */
export function StaffMemberPage() {
  const { t } = useTranslation('admin');
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { roleName, permissionName } = useCatalogue();
  const [entry, setEntry] = useState<AuditLogEntry | null>(null);
  const result = useApi((signal) => api.getStaffMember(id, { signal }), [id]);
  const name = result.status === 'success' ? result.data.staff.display_name : '';
  useDocumentTitle(name || t('staff.title'));
  const crumbs = [...useAdminCrumbs(), { label: t('staff.title'), to: '/admin/staff' }, { label: name || '…' }];

  if (result.status === 'loading') {
    return (
      <>
        <PageHeader title={t('staff.title')} crumbs={crumbs} />
        <Loading rows={6} />
      </>
    );
  }
  if (result.status === 'error') {
    return (
      <>
        <PageHeader title={t('staff.title')} crumbs={crumbs} />
        <DataError error={result.error} onRetry={result.reload} title={t('member.loadError')} />
      </>
    );
  }

  const { staff, sessions, recent_audit, pending_approvals } = result.data;

  const roleColumns: Column<StaffRoleGrant>[] = [
    {
      key: 'role',
      header: t('member.roleCol.role'),
      cell: (g) => (
        <Link to={`/admin/roles/${encodeURIComponent(g.role_code)}`} title={g.role_code} className="font-medium text-primary hover:underline">
          {roleName(g.role_code, g.role_description)}
        </Link>
      ),
    },
    {
      key: 'scope',
      header: t('member.roleCol.scope'),
      cell: (g) =>
        g.scope_type === 'GLOBAL' ? (
          t('scope.global')
        ) : (
          <RecordRef resourceType="customer" resourceId={g.scope_id} resourceName={g.scope_name} />
        ),
    },
    { key: 'granted', header: t('member.roleCol.granted'), cell: (g) => <span title={formatDateTime(g.granted_at)}>{formatRelative(g.granted_at)}</span> },
  ];

  const sessionColumns: Column<StaffSessionSummary>[] = [
    { key: 'started', header: t('member.sessionCol.started'), cell: (s) => <span title={formatDateTime(s.created_at)}>{formatRelative(s.created_at)}</span> },
    { key: 'expires', header: t('member.sessionCol.expires'), cell: (s) => <span title={formatDateTime(s.expires_at)}>{formatRelative(s.expires_at)}</span> },
    { key: 'mfa', header: t('member.sessionCol.mfa'), cell: (s) => <Badge tone={s.mfa_verified ? 'ok' : 'warn'}>{s.mfa_verified ? t('member.mfaVerified') : t('member.mfaNotVerified')}</Badge> },
    { key: 'from', header: t('member.sessionCol.from'), cell: (s) => (s.ip ? <code className="text-xs">{s.ip}</code> : '—') },
    {
      key: 'device',
      header: t('member.sessionCol.device'),
      // Table cells do not wrap by default; a user agent string is long enough to need it. The
      // minimum width goes on the content: the table is `w-full`, so a cell width is dropped on
      // a narrow screen and the text collapses to one word per line.
      className: 'whitespace-normal',
      cell: (s) => <span className="block min-w-56 text-xs text-muted-foreground">{s.user_agent ?? '—'}</span>,
    },
  ];

  const auditColumns: Column<AuditLogEntry>[] = [
    { key: 'time', header: t('audit.col.time'), cell: (a) => <span title={formatDateTime(a.created_at)}>{formatRelative(a.created_at)}</span> },
    { key: 'action', header: t('audit.col.action'), cell: (a) => <ActionLabel action={a.action} /> },
    { key: 'resource', header: t('audit.col.resource'), cell: (a) => <RecordRef resourceType={a.resource_type} resourceId={a.resource_id} resourceName={a.resource_name} /> },
    { key: 'outcome', header: t('audit.col.outcome'), cell: (a) => <Badge tone={OUTCOME_TONE[a.outcome]}>{t(`auditOutcome.${a.outcome}`)}</Badge> },
    {
      key: 'reason',
      header: t('audit.col.reason'),
      className: 'whitespace-normal',
      cell: (a) => (
        <span className="block min-w-64 text-sm">
          <AuditReason reason={a.reason} />
        </span>
      ),
    },
  ];

  const approvalColumns: Column<StaffPendingApproval>[] = [
    {
      key: 'change',
      header: t('approvals.col.change'),
      className: 'whitespace-normal',
      cell: (a) => (
        <span>
          <span className="font-medium">{permissionName(a.action, a.action_description)}</span>
          <span className="block text-xs font-normal text-muted-foreground">{a.action}</span>
        </span>
      ),
    },
    { key: 'resource', header: t('approvals.col.resource'), cell: (a) => <RecordRef resourceType={a.resource_type} resourceId={a.resource_id} resourceName={a.resource_name} /> },
    { key: 'reason', header: t('approvals.dialog.reason'), className: 'whitespace-normal', cell: (a) => <span className="block min-w-64 text-sm">{a.reason}</span> },
    { key: 'age', header: t('approvals.col.age'), cell: (a) => <span title={formatDateTime(a.requested_at)}>{formatRelative(a.requested_at)}</span> },
  ];

  return (
    <>
      <PageHeader
        title={staff.display_name}
        crumbs={crumbs}
        subtitle={staff.email}
        actions={
          <>
            <Link to={`/admin/audit?actor_id=${encodeURIComponent(staff.id)}`} className="text-sm font-medium text-primary hover:underline">
              {t('member.allActivity')}
            </Link>
            <StaffAccessActions member={staff} onChanged={result.reload} />
          </>
        }
      />
      <div className="space-y-4">
        <Callout tone="info">{t('note.noSecrets')}</Callout>
        <AccessChangesPanel userId={staff.id} reloadKey={result.data} onChanged={result.reload} />
        <Section title={t('member.overview')}>
          <KeyValue
            columns={3}
            rows={[
              [t('member.email'), staff.email],
              [t('member.state'), <Badge key="s" tone={STATUS_TONE[staff.status]}>{t(`staffStatus.${staff.status}`)}</Badge>],
              [t('member.signIn'), <SignInBadges key="m" member={staff} />],
              [t('member.lastLogin'), staff.last_login_at ? formatDateTime(staff.last_login_at) : t('staff.neverSignedIn')],
              [t('member.failed'), staff.failed_logins],
              [t('member.created'), formatDateTime(staff.created_at)],
              [t('member.id'), <CopyText key="id" value={staff.id} />],
            ]}
          />
        </Section>

        <Section flush title={t('member.roles')} description={t('member.rolesHint')}>
          {staff.roles.length === 0 ? (
            <div className="px-5 pb-5">
              <Callout tone="warn">{t('member.noRoles')}</Callout>
            </div>
          ) : (
            <DataTable columns={roleColumns} rows={staff.roles} rowKey={(g) => `${g.role_code}/${g.scope_type}/${g.scope_id ?? ''}`} />
          )}
        </Section>

        <Section flush title={t('member.sessions')} description={t('member.sessionsHint')}>
          {sessions.length === 0 ? (
            <div className="px-5 pb-5">
              <EmptyState title={t('member.noSessions')} />
            </div>
          ) : (
            <DataTable columns={sessionColumns} rows={sessions} rowKey={(s) => s.id} />
          )}
        </Section>

        {pending_approvals.length > 0 ? (
          <Section flush title={t('member.ownRequests')} description={t('member.ownRequestsHint')}>
            <DataTable
              columns={approvalColumns}
              rows={pending_approvals}
              rowKey={(a) => a.id}
              onRowClick={(a) => navigate(`/admin/approvals?request=${encodeURIComponent(a.id)}`)}
            />
          </Section>
        ) : null}

        <Section flush title={t('member.recentAudit')} description={t('member.recentAuditHint')}>
          {recent_audit.length === 0 ? (
            <div className="px-5 pb-5">
              <EmptyState title={t('member.noAudit')} />
            </div>
          ) : (
            <DataTable columns={auditColumns} rows={recent_audit} rowKey={(a) => a.id} onRowClick={setEntry} />
          )}
        </Section>
      </div>
      <EntryDrawer entry={entry} onClose={() => setEntry(null)} />
    </>
  );
}
